import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, 
  Clock, 
  Users, 
  ArrowRight, 
  Menu, 
  X,
  Server,
  Lock,
  Headphones
} from 'lucide-react';

// Import images
import datacenterImg from '../images/datacenter.jpeg';
import networkTechImg from '../images/network tech.jpeg';
import wiringImg from '../images/wiring.jpeg';
import support247Img from '../images/247.jpeg';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <Server className="w-8 h-8" />,
      title: "Cloud Infrastructure Monitoring",
      description: "Real-time monitoring of your cloud infrastructure with proactive alerts and automated responses to keep your systems running smoothly."
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Security & Compliance", 
      description: "Enterprise-grade security protocols and compliance management to protect your data and meet industry standards."
    },
    {
      icon: <Headphones className="w-8 h-8" />,
      title: "24/7 Expert Support",
      description: "Round-the-clock technical support from certified cloud and infrastructure professionals ready to assist you anytime."
    }
  ];

  const plans = [
    {
      name: "Remote Support",
      price: "$199",
      period: "/month",
      description: "Essential remote IT support for your business",
      features: [
        "Remote technical support",
        "Email & phone assistance",
        "Software troubleshooting",
        "Basic system monitoring",
        "Business hours coverage"
      ],
      popular: false
    },
    {
      name: "Network Plus", 
      price: "$249",
      period: "/month",
      description: "Remote support with network infrastructure",
      features: [
        "Everything in Remote Support",
        "Router & switch management",
        "ISP coordination & support",
        "Network configuration",
        "Priority response times",
        "Network health monitoring"
      ],
      popular: true
    },
    {
      name: "Complete Connectivity",
      price: "$299", 
      period: "/month",
      description: "Full network management with WiFi upgrade",
      features: [
        "Everything in Network Plus",
        "WiFi infrastructure upgrade",
        "Dual connectivity management",
        "Wired & wireless optimization",
        "Advanced network security",
        "Proactive monitoring",
        "Priority 24/7 support"
      ],
      popular: false
    }
  ];

  const stats = [
    { number: "1000+", label: "Businesses Served" },
    { number: "99.9%", label: "Uptime Guarantee" },
    { number: "24/7", label: "Support Availability" },
    { number: "15+", label: "Years Experience" }
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className={`navbar ${isScrolled ? 'shadow-lg' : ''}`}>
        <div className="container">
          <div className="navbar-content">
            {/* Logo */}
            <a href="/" className="logo">
              Grid Managed Services
            </a>
            
            {/* Desktop Navigation */}
            <ul className="desktop-nav">
              <li><a href="#features" className="nav-link">Services</a></li>
              <li><a href="#pricing" className="nav-link">Pricing</a></li>
              <li><a href="#about" className="nav-link">About</a></li>
              <li><a href="#contact" className="nav-link">Contact</a></li>
              <li>
                <button 
                  onClick={() => window.location.href = process.env.REACT_APP_CUSTOMER_PORTAL_URL || '/dashboard'}
                  className="btn btn-outline btn-sm"
                >
                  Sign In
                </button>
              </li>
            </ul>

            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-btn"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown */}
        {isMenuOpen && (
          <div className="mobile-menu">
            <div className="container py-4">
              <div className="flex flex-col gap-4">
                <a 
                  href="#features" 
                  className="nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Services
                </a>
                <a 
                  href="#pricing" 
                  className="nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Pricing
                </a>
                <a 
                  href="#about" 
                  className="nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  About
                </a>
                <a 
                  href="#contact" 
                  className="nav-link"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Contact
                </a>
                <button 
                  onClick={() => {
                    window.location.href = process.env.REACT_APP_CUSTOMER_PORTAL_URL || '/dashboard';
                    setIsMenuOpen(false);
                  }}
                  className="btn btn-outline"
                >
                  Sign In
                </button>
                <button 
                  onClick={() => {
                    navigate('/subscription');
                    setIsMenuOpen(false);
                  }}
                  className="btn btn-primary"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content fade-in-up">
            <h1 className="hero-title">
              Professional IT Support for Your Business
            </h1>
            <p className="hero-subtitle">
              Comprehensive IT support, cybersecurity, and cloud monitoring solutions with 24/7 availability. Focus on your business while we handle your technology.
            </p>
            <div className="hero-buttons">
              <button 
                onClick={() => navigate('/subscription')}
                className="btn btn-primary btn-lg"
              >
                Get Started
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
              <button 
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                className="btn btn-secondary btn-lg"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section-sm bg-white">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <div key={index} className="fade-in-up">
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Why Businesses Choose Grid Managed Services</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive cloud monitoring and IT solutions with 24/7 support, 
              delivering the expertise and reliability your business needs to thrive.
            </p>
          </div>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card fade-in-up">
                <div className="feature-icon">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IT Services Section */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Our Services Work When You Don't</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              When you choose our managed IT services, you receive 24/7 monitoring and support for your network. 
              We respond the moment something occurs, so you can focus on your business.
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {/* Data Center Monitoring - Image Left */}
            <div className="service-row">
              <div className="service-image-small">
                <img 
                  src={datacenterImg} 
                  alt="Secure Data Center Infrastructure" 
                  className="image-cover"
                />
              </div>
              <div className="service-content-large">
                <h3 className="text-xl font-bold mb-3">Enterprise Infrastructure & Monitoring</h3>
                <ul className="service-list">
                  <li>Hardware and bandwidth monitoring</li>
                  <li>System asset management</li>
                  <li>99.9% uptime guarantee</li>
                  <li>Immediate alert system if issues detected</li>
                </ul>
              </div>
            </div>

            {/* Network Technician Support - Image Right */}
            <div className="service-row service-row-reverse">
              <div className="service-image-small">
                <img 
                  src={networkTechImg} 
                  alt="Expert Network Technicians" 
                  className="image-cover"
                />
              </div>
              <div className="service-content-large">
                <h3 className="text-xl font-bold mb-3">Expert Technical Support</h3>
                <ul className="service-list">
                  <li>Certified IT professionals on call 24/7</li>
                  <li>Onsite and remote support services</li>
                  <li>Dedicated account management</li>
                  <li>Proactive problem resolution</li>
                </ul>
              </div>
            </div>

            {/* Network Infrastructure - Image Left */}
            <div className="service-row">
              <div className="service-image-small">
                <img 
                  src={wiringImg} 
                  alt="Professional Network Infrastructure" 
                  className="image-cover"
                />
              </div>
              <div className="service-content-large">
                <h3 className="text-xl font-bold mb-3">Network Management & Updates</h3>
                <ul className="service-list">
                  <li>Software updates and patch management</li>
                  <li>Network equipment installation and setup</li>
                  <li>Software licensing management</li>
                  <li>Complete infrastructure optimization</li>
                </ul>
              </div>
            </div>

            {/* 24/7 Support - Image Right */}
            <div className="service-row service-row-reverse">
              <div className="service-image-small">
                <img 
                  src={support247Img} 
                  alt="24/7 Technical Support" 
                  className="image-cover"
                />
              </div>
              <div className="service-content-large">
                <h3 className="text-xl font-bold mb-3">Backup & Security Services</h3>
                <ul className="service-list">
                  <li>Backup monitoring and restore verification</li>
                  <li>Disaster recovery planning</li>
                  <li>Security updates and compliance</li>
                  <li>Data protection and encryption</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Choose Your IT Support Plan</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Select the perfect IT support plan for your business. 
              All plans include our core services with varying levels of support.
            </p>
          </div>
          
          <div className="pricing-grid">
            {plans.map((plan, index) => (
              <div key={index} className={`pricing-card ${plan.popular ? 'featured' : ''}`}>
                {plan.popular && (
                  <div className="pricing-badge">Most Popular</div>
                )}
                
                <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                <p className="text-gray-600 mb-4">{plan.description}</p>
                
                <div className="pricing-price">
                  {plan.price}
                  <span className="text-lg font-normal text-gray-600">{plan.period}</span>
                </div>
                
                <ul className="pricing-features">
                  {plan.features.map((feature, featureIndex) => (
                    <li key={featureIndex}>{feature}</li>
                  ))}
                </ul>
                
                <button 
                  onClick={() => {
                    const planId = index === 0 ? 'basic' : index === 1 ? 'professional' : 'enterprise';
                    navigate(`/subscription?plan=${planId}`);
                  }}
                  className={`btn w-full ${plan.popular ? 'btn-primary' : 'btn-outline'}`}
                >
                  Get Started
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section bg-gray-50">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">About Grid Managed Services</h2>
              <div className="space-y-6">
                <div>
                  <h3 className="text-xl font-semibold mb-2 flex items-center">
                    <Shield className="w-6 h-6 mr-2 text-blue-600" />
                    Our Mission
                  </h3>
                  <p className="text-gray-600">
                    We provide reliable, secure, and comprehensive IT support services to businesses 
                    of all sizes. Our mission is to ensure your technology infrastructure 
                    operates smoothly, securely, and efficiently with 24/7 monitoring and support.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-2 flex items-center">
                    <Users className="w-6 h-6 mr-2 text-blue-600" />
                    Our Experience
                  </h3>
                  <p className="text-gray-600">
                    With over 15 years of experience serving businesses across multiple industries, 
                    we understand the unique challenges of modern technology infrastructure. Our team 
                    holds the highest security clearances and certifications.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-xl font-semibold mb-2 flex items-center">
                    <Clock className="w-6 h-6 mr-2 text-blue-600" />
                    24/7 Availability
                  </h3>
                  <p className="text-gray-600">
                    Our support team is available around the clock to ensure your business 
                    never stops. Emergency support, proactive monitoring, and rapid response 
                    times are standard with all our plans.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="card text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">FedRAMP</div>
                <div className="text-sm text-gray-600">Certified</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">ISO 27001</div>
                <div className="text-sm text-gray-600">Compliant</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">SOC 2</div>
                <div className="text-sm text-gray-600">Type II</div>
              </div>
              <div className="card text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">NIST</div>
                <div className="text-sm text-gray-600">Framework</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your IT Operations?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Join hundreds of businesses who trust us with their technology. 
            Get started with a free consultation today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/subscription')}
              className="btn btn-secondary btn-lg"
            >
              Get Started
            </button>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="btn btn-outline btn-lg text-white border-white hover:bg-white hover:text-red-600"
            >
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="footer">
        <div className="container">
          <div className="footer-main">
            <div className="footer-column footer-about">
              <h3 className="footer-heading">Grid Managed Services</h3>
              <p className="footer-description">
                Professional IT services with 24/7 cloud monitoring and support. 
                Secure, reliable, and always available when you need us.
              </p>
              <div className="footer-contact">
                <div className="footer-contact-item">
                  <span className="footer-icon">📍</span>
                  <span>Washington, DC Metro Area</span>
                </div>
                <div className="footer-contact-item">
                  <span className="footer-icon">📞</span>
                  <a href="tel:5551234357" className="footer-link">(555) 123-HELP</a>
                </div>
                <div className="footer-contact-item">
                  <span className="footer-icon">✉️</span>
                  <a href="mailto:info@gridmanagedservices.com" className="footer-link">info@gridmanagedservices.com</a>
                </div>
              </div>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-heading">Services</h4>
              <ul className="footer-links">
                <li><a href="#features" className="footer-link">Managed IT Services</a></li>
                <li><a href="#features" className="footer-link">24/7 Monitoring & Support</a></li>
                <li><a href="#features" className="footer-link">Cybersecurity</a></li>
                <li><a href="#features" className="footer-link">Cloud Infrastructure</a></li>
                <li><a href="#features" className="footer-link">Backup & Recovery</a></li>
                <li><a href="#features" className="footer-link">Compliance Management</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-heading">Company</h4>
              <ul className="footer-links">
                <li><a href="#about" className="footer-link">About Us</a></li>
                <li><a href="#pricing" className="footer-link">Pricing</a></li>
                <li><a href="#contact" className="footer-link">Contact</a></li>
                <li><a href="https://portal.gridmanagedservices.com" className="footer-link">Client Sign In</a></li>
                <li><a href="/support" className="footer-link">Support Portal</a></li>
                <li><a href="/careers" className="footer-link">Careers</a></li>
              </ul>
            </div>
            
            <div className="footer-column">
              <h4 className="footer-heading">Resources</h4>
              <ul className="footer-links">
                <li><a href="/knowledge-base" className="footer-link">Knowledge Base</a></li>
                <li><a href="/best-practices" className="footer-link">IT Best Practices</a></li>
                <li><a href="/security-tips" className="footer-link">Security Tips</a></li>
                <li><a href="/case-studies" className="footer-link">Case Studies</a></li>
                <li><a href="/blog" className="footer-link">Blog</a></li>
                <li><a href="/newsletter" className="footer-link">Newsletter</a></li>
              </ul>
            </div>
          </div>
          
          <div className="footer-bottom">
            <div className="footer-bottom-content">
              <p className="footer-copyright">
                &copy; 2026 Grid Managed Services. All rights reserved.
              </p>
              <div className="footer-legal">
                <a href="/privacy" className="footer-link">Privacy Policy</a>
                <span className="footer-separator">|</span>
                <a href="/terms" className="footer-link">Terms of Service</a>
                <span className="footer-separator">|</span>
                <a href="/cookies" className="footer-link">Cookie Policy</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;